import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

// True while `text`, set in the referenced element's own font, is wider than the
// element's content box — i.e. it would be clipped. Lets a squeezed pill swap its long
// placeholder for a short one only when the long one does not fit (design changelog
// M4: "Search booki…" in the top bar at 1024, "…booking c" in My bookings), so wider
// layouts and shorter translations keep the full text. Re-measured on resize and once
// the web fonts load. Without ResizeObserver or a 2D canvas (jsdom) it stays false.
let canvas: HTMLCanvasElement | null = null;

function textWidth(text: string, el: HTMLElement): number | null {
  canvas ??= document.createElement('canvas');
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx) return null;
  const cs = getComputedStyle(el);
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return ctx.measureText(text).width;
}

export function useTextOverflow(ref: RefObject<HTMLElement | null>, text: string): boolean {
  const [over, setOver] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let live = true;
    const measure = () => {
      if (!live) return;
      const cs = getComputedStyle(el);
      const box = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const w = textWidth(text, el);
      setOver(w !== null && box > 0 && w > box);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    void document.fonts?.ready.then(measure);
    return () => {
      live = false;
      ro.disconnect();
    };
  }, [ref, text]);
  return over;
}
